import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Lock, Zap, Headphones, Layers } from 'lucide-react';
// Parametrized upsells — add future upsells here using the same pattern.
const UPSELLS = [{
  id: 'meditations',
  Icon: Headphones,
  title: 'Agregar Biblioteca Profesional de Meditaciones',
  increment: '+ $9.000',
  summaryLabel: '🎧 Incluye Biblioteca Profesional de Meditaciones',
  support: 'Ideal para transformar tus clases, Savasanas y cierres en experiencias más profundas.',
  includes: ['5 meditaciones listas para usar', 'Audios instrumentales profesionales', '1 meditación guiada completa', 'Guía profesional en PDF', '10 guiones de meditación listos para leer o grabar']
}, {
  id: 'flashcards',
  Icon: Layers,
  title: 'Agregar 45 FlashCards Premium',
  increment: '+ $9.000',
  summaryLabel: '🃏 Incluye 45 FlashCards Premium',
  support: 'Perfectas para guiar a tus alumnos y armar secuencias de forma visual.',
  includes: ['45 FlashCards de posturas (asanas)', 'Listas para imprimir y plastificar', 'Nombres en sánscrito y español', 'Indicaciones y beneficios de cada postura', 'Ideales para clases, secuencias y material de apoyo']
}] as const;
// Each combination maps to the exact product info to show + checkout URL.
const COMBOS: Record<string, {
  name: string;
  price: string;
  comparePrice: string;
  button: string;
  url: string;
}> = {
  '': {
    name: 'Mega Pack Yoga Listo 2026',
    price: '$19.990',
    comparePrice: '$64.990',
    button: 'QUIERO MI MEGA PACK',
    url: 'https://www.iterapro.shop/cart/43417960808537:1'
  },
  meditations: {
    name: 'Mega Pack Yoga Listo 2026 + Biblioteca Profesional de Meditaciones',
    price: '$28.990',
    comparePrice: '$97.990',
    button: 'QUIERO EL SISTEMA COMPLETO',
    url: 'https://www.iterapro.shop/cart/43428592844889:1'
  },
  flashcards: {
    name: 'Mega Pack Yoga Listo 2026 + 45 FlashCards Premium',
    price: '$28.990',
    comparePrice: '$97.990',
    button: 'QUIERO EL SISTEMA COMPLETO',
    url: 'https://www.iterapro.shop/cart/43438842151001:1'
  },
  'meditations+flashcards': {
    name: 'Sistema Profesional Yoga Listo 2026',
    price: '$37.990',
    comparePrice: '$129.990',
    button: 'QUIERO EL SISTEMA COMPLETO',
    url: 'https://www.iterapro.shop/cart/43438855258201:1'
  }
};
export function Pricing() {
  const features = ['6 Tomos completos', '6 Bonos exclusivos', 'Actualizaciones futuras', 'Acceso inmediato'];
  const [selected, setSelected] = useState<Record<string, boolean>>({
    meditations: false,
    flashcards: false
  });
  const toggle = (id: string, value: boolean) => setSelected(prev => ({
    ...prev,
    [id]: value
  }));
  // Build the combination key in the canonical order defined by UPSELLS.
  const activeUpsells = UPSELLS.filter(u => selected[u.id]);
  const comboKey = activeUpsells.map(u => u.id).join('+');
  const combo = COMBOS[comboKey] ?? COMBOS[''];
  const productName = combo.name;
  const currentPrice = combo.price;
  const comparePrice = combo.comparePrice;
  const buttonText = combo.button;
  const checkoutUrl = combo.url;
  const hasUpsell = activeUpsells.length > 0;
  return <section id="pricing" className="py-24 lg:py-32 bg-accent-50 relative overflow-hidden" data-id="element-147">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#FAF8F5] rounded-full blur-3xl opacity-80 pointer-events-none" data-id="element-148"></div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-149">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-150">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-900 text-white font-medium text-xs uppercase tracking-widest mb-8 shadow-sm" data-id="element-151">
            <Zap className="w-4 h-4 text-accent-400" data-id="element-152" />
            Oferta · Últimos cupos
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-ink-900 tracking-tight leading-tight" data-id="element-153">
            Reservá tu acceso al{' '}
            <span className="text-brand-600 italic" data-id="element-154">Mega Pack</span>
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white rounded-[2.5rem] p-8 md:p-14 shadow-md border border-accent-100/50 relative max-w-xl mx-auto" data-id="element-155">
          {/* Top tag */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-brand-700 text-white font-medium px-6 py-2 rounded-full text-xs uppercase tracking-widest shadow-sm whitespace-nowrap" data-id="element-156">
            Acceso completo
          </div>

          <div className="text-center mb-10 pt-4" data-id="element-157">
            <div className="text-ink-900 font-serif font-medium text-2xl mb-2 transition-all" data-id="element-158">
              {productName}
            </div>
            <p className="text-ink-500 text-sm mb-8 font-light" data-id="element-159">
              6 Tomos + 6 Bonos + Garantía de 7 días
            </p>

            <div className="mb-3 flex items-center justify-center gap-4" data-id="element-160">
              <span className="text-ink-400 line-through text-2xl font-light" data-id="element-161">
                {comparePrice}
              </span>
              <span className="bg-accent-50 text-accent-700 font-medium text-xs px-3 py-1.5 rounded-full uppercase tracking-widest" data-id="element-162">
                -70%
              </span>
            </div>
            <div className="text-5xl sm:text-6xl md:text-7xl font-serif font-medium text-brand-900 mb-3 tracking-tight" data-id="element-163">
              {currentPrice}
            </div>
            <AnimatePresence data-id="element-164">
              {hasUpsell && <motion.p initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="text-xs text-brand-700 font-medium mb-2" data-id="element-165">
                  Incluye Mega Pack
                  {activeUpsells.map(u => u.id === 'meditations' ? ' + Biblioteca Profesional de Meditaciones' : ' + 45 FlashCards Premium')}
                </motion.p>}
            </AnimatePresence>
            <p className="text-sm text-ink-500 font-light" data-id="element-166">
              Pago único · Sin suscripción
            </p>
          </div>

          <div className="space-y-4 mb-10 bg-[#FAF8F5] rounded-[2rem] p-8" data-id="element-167">
            {features.map((feature, i) => <div key={i} className="flex items-center gap-4" data-id="element-168">
                <div className="w-6 h-6 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0" data-id="element-169">
                  <Check className="w-3.5 h-3.5" strokeWidth={2} data-id="element-170" />
                </div>
                <span className="text-ink-800 font-medium text-base" data-id="element-171">
                  {feature}
                </span>
              </div>)}
          </div>

          {/* Optional upgrades */}
          <div className="space-y-4 mb-8" data-id="element-172">
            {UPSELLS.map(upsell => {
            const Icon = upsell.Icon;
            const isOn = selected[upsell.id];
            return <label key={upsell.id} htmlFor={`upsell-${upsell.id}`} className={`block cursor-pointer rounded-[1.75rem] border p-5 sm:p-6 transition-all ${isOn ? 'border-accent-300 bg-accent-50/70 shadow-sm' : 'border-accent-100 bg-[#FAF8F5] hover:border-accent-200'}`} data-id="element-173">
                  <div className="flex items-start gap-4" data-id="element-174">
                    <div className="relative flex items-center shrink-0 pt-0.5" data-id="element-175">
                      <input id={`upsell-${upsell.id}`} type="checkbox" checked={isOn} onChange={e => toggle(upsell.id, e.target.checked)} className="peer sr-only" data-id="element-176" />
                      <span className="w-6 h-6 rounded-md border-2 border-accent-300 bg-white flex items-center justify-center transition-all peer-checked:bg-brand-700 peer-checked:border-brand-700" aria-hidden="true" data-id="element-177">
                        {isOn && <Check className="w-4 h-4 text-white" strokeWidth={2.5} data-id="element-178" />}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0 text-left" data-id="element-179">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1.5 sm:gap-3" data-id="element-180">
                        <span className="font-medium text-ink-900 text-[15px] sm:text-base flex items-start gap-2" data-id="element-181">
                          <Icon className="w-4 h-4 mt-1 text-accent-600 shrink-0" strokeWidth={1.75} data-id="element-182" />
                          {upsell.title}
                        </span>
                        <span className="font-serif font-medium text-brand-700 text-lg shrink-0 pl-6 sm:pl-0" data-id="element-183">
                          {upsell.increment}
                        </span>
                      </div>

                      <ul className="mt-3 space-y-1.5" data-id="element-184">
                        {upsell.includes.map((item, i) => <li key={i} className="flex items-start gap-2 text-[13px] text-ink-600 font-light leading-snug" data-id="element-185">
                            <span className="mt-1.5 w-1 h-1 rounded-full bg-accent-400 shrink-0" data-id="element-186" />
                            {item}
                          </li>)}
                      </ul>

                      <p className="mt-3 text-[12px] text-ink-400 font-light italic leading-snug" data-id="element-187">
                        {upsell.support}
                      </p>
                    </div>
                  </div>
                </label>;
          })}
          </div>

          {/* Dynamic total summary */}
          <div className="text-center mb-4" data-id="element-188">
            <p className="font-serif font-medium text-ink-800 text-lg" data-id="element-189">
              Total hoy: <span className="text-brand-700" data-id="element-190">{currentPrice}</span>
            </p>
            <div className="mt-1 space-y-0.5" data-id="element-191">
              <AnimatePresence data-id="element-192">
                {activeUpsells.map(u => <motion.p key={u.id} initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="text-xs text-ink-500 font-light" data-id="element-193">
                    {u.summaryLabel}
                  </motion.p>)}
              </AnimatePresence>
            </div>
          </div>

          <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="block w-full text-center bg-brand-700 hover:bg-brand-800 text-white font-medium py-5 px-6 rounded-full transition-all transform hover:-translate-y-0.5 shadow-md hover:shadow-lg text-lg tracking-wide" data-id="element-194">
            {buttonText}
          </a>

          <p className="text-center text-xs text-ink-400 mt-6 inline-flex items-center justify-center gap-2 w-full font-light" data-id="element-195">
            <Lock className="w-3.5 h-3.5" data-id="element-196" />
            Pago 100% seguro · Mercado Pago · Tarjeta · Transferencia
          </p>
        </motion.div>
      </div>
    </section>;
}